import { useMemo, useState } from 'react';
import { useTheme } from '@/store/theme-context';
import { cn } from '@/lib/cn';
import type { CatalogCategory, CatalogResponse, DotplotUser } from '@/types/analytics';
import {
  categoryColor,
  dotSizePx,
  formatDayLabel,
  isAutomationOnly,
  isWeekendUTC,
  sortedEventBreakdown,
  userCategoryTotals,
  userDisplayName,
} from '@/lib/analytics/dotplot';
import { AutomationDot } from './AutomationDot';

interface UserDotPlotProps {
  user: DotplotUser;
  days: string[];
  catalog: CatalogResponse;
}

interface TipState {
  category: CatalogCategory;
  day: string;
  rect: DOMRect;
}

/**
 * Single-user focus grid: rows = activity categories, columns = days. A
 * transpose of the multi-user split view for one person — each row is drawn in
 * its category color and sized by that category's daily count. The onboarding
 * day tints the whole column (it's a per-day marker, independent of category).
 */
export function UserDotPlot({ user, days, catalog }: UserDotPlotProps) {
  const { resolvedTheme } = useTheme();
  const [tip, setTip] = useState<TipState | null>(null);

  const totals = useMemo(
    () => userCategoryTotals(user, catalog.categories.map((c) => c.key)).totals,
    [user, catalog],
  );

  return (
    <div className="relative">
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="border-separate border-spacing-0">
          <thead>
            <tr>
              <th
                scope="col"
                className="sticky left-0 z-10 bg-card px-3 py-2 text-left text-xs font-medium text-muted-foreground"
              >
                Category
              </th>
              {days.map((day) => {
                const onboarding = !!user.cells[day]?.firstSeen;
                return (
                  <th
                    key={day}
                    scope="col"
                    className={cn(
                      'h-10 min-w-[26px] px-0 py-1 text-center align-bottom text-[10px] font-medium text-muted-foreground',
                      onboarding
                        ? 'bg-amber-400/20'
                        : isWeekendUTC(day) && 'bg-muted/50',
                    )}
                  >
                    <span className="inline-block origin-center -rotate-45 whitespace-nowrap">
                      {formatDayLabel(day)}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {catalog.categories.map((category) => {
              const color = categoryColor(category, resolvedTheme) ?? 'hsl(var(--muted-foreground))';
              return (
                <tr key={category.key} className="group">
                  <th
                    scope="row"
                    className="sticky left-0 z-10 bg-card px-3 py-1.5 text-left font-normal group-hover:bg-muted/40"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ background: color }}
                      />
                      <span className="text-xs text-foreground">{category.label}</span>
                      <span className="ml-auto pl-3 text-[11px] tabular-nums text-muted-foreground">
                        {(totals[category.key] ?? 0).toLocaleString()}
                      </span>
                    </div>
                  </th>
                  {days.map((day) => {
                    const cell = user.cells[day];
                    const count = cell?.byCategory?.[category.key] ?? 0;
                    const onboarding = !!cell?.firstSeen;
                    const tdClass = cn(
                      'h-[26px] min-w-[26px] text-center',
                      onboarding
                        ? 'bg-amber-400/20'
                        : isWeekendUTC(day) && 'bg-muted/50',
                    );
                    if (count <= 0) {
                      return (
                        <td
                          key={day}
                          className={tdClass}
                          title={onboarding ? 'Onboarding day' : undefined}
                        >
                          {cell && isAutomationOnly(cell) && (
                            <AutomationDot cell={cell} day={day} />
                          )}
                        </td>
                      );
                    }
                    const size = dotSizePx(count);
                    return (
                      <td key={day} className={tdClass}>
                        <span
                          tabIndex={0}
                          role="img"
                          aria-label={`${category.label}, ${day}: ${count} event${count === 1 ? '' : 's'}${onboarding ? ', onboarding day' : ''}`}
                          onMouseEnter={(e) =>
                            setTip({ category, day, rect: e.currentTarget.getBoundingClientRect() })
                          }
                          onMouseLeave={() => setTip(null)}
                          onFocus={(e) =>
                            setTip({ category, day, rect: e.currentTarget.getBoundingClientRect() })
                          }
                          onBlur={() => setTip(null)}
                          className="inline-block cursor-default rounded-full align-middle outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          style={{ width: size, height: size, background: color }}
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {tip && <CellTooltip tip={tip} user={user} />}
    </div>
  );
}

function CellTooltip({ tip, user }: { tip: TipState; user: DotplotUser }) {
  const cell = user.cells[tip.day];
  if (!cell) return null;
  const count = cell.byCategory?.[tip.category.key] ?? 0;
  const catEvents = new Set(tip.category.events);
  const breakdown = sortedEventBreakdown(cell).filter(([event]) => catEvents.has(event));

  const placeBelow = tip.rect.top - 8 < 120;
  const top = placeBelow ? tip.rect.bottom + 8 : tip.rect.top - 8;

  return (
    <div
      role="tooltip"
      className="pointer-events-none fixed z-50 max-w-[340px] -translate-x-1/2 rounded-md border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-lg"
      style={{
        left: tip.rect.left + tip.rect.width / 2,
        top,
        transform: `translate(-50%, ${placeBelow ? '0' : '-100%'})`,
      }}
    >
      <div className="font-medium text-foreground">{userDisplayName(user)}</div>
      <div className="mt-0.5 text-muted-foreground">
        {tip.day} · {tip.category.label}: {count} event{count === 1 ? '' : 's'}
        {cell.firstSeen && ' · ★ onboarding day'}
      </div>
      {breakdown.length > 0 && (
        <dl className="mt-1.5 grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5">
          {breakdown.map(([event, n]) => (
            <div key={event} className="contents">
              <dt className="truncate text-muted-foreground">{event}</dt>
              <dd className="text-right font-mono tabular-nums text-foreground">{n}</dd>
            </div>
          ))}
        </dl>
      )}
      {(cell.automation ?? 0) > 0 && (
        <div className="mt-1 text-muted-foreground">
          ◦ automation: {cell.automation}
        </div>
      )}
    </div>
  );
}

export default UserDotPlot;
